import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { PlusIcon, WorkflowIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { EmptyState } from '@/shared/components/EmptyState';
import { RetryButton } from '@/shared/components/RetryButton';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { Skeleton } from '@/components/ui/skeleton';
import { PagePlaceholder } from '@/shared/components/PagePlaceholder';
import { countDeclaredSteps, useWorkflows } from '../hooks/useWorkflows';
import { workflowDestination } from '../workflowDestination';
import type { WorkflowStatusFilter } from '../searchParams';
import { AddWorkflowMenu } from './AddWorkflowMenu';
import { CreateWorkflowDialog } from './CreateWorkflowDialog';
import { UploadWorkflowDialog } from './UploadWorkflowDialog';
import { PendingChangesMarker } from './PendingChangesMarker';
import { WorkflowName } from './WorkflowName';
import { WorkflowRowMenu } from './WorkflowRowMenu';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * The three controls the page offers, against the two values the address
 * carries: "All" is the absence of the parameter, not a value of it.
 */
const STATUS_FILTERS: {
  value: WorkflowStatusFilter | undefined;
  label: string;
}[] = [
  { value: undefined, label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'drafts', label: 'Drafts' },
];

/**
 * The Workflows screen: list, explanatory empty state, and the two entry
 * points into the one add-workflow menu (FR-001…003). Both the header "+" and
 * the empty state's call to action open the same menu; the menu's single
 * action opens the one upload dialog instance this page owns.
 *
 * The three body states are disjoint (FR-007): loading, load-failed with a
 * retry, and loaded — where an empty list is a real empty state, never a
 * blank list and never a failure in disguise.
 */
export interface WorkflowListPageProps {
  workspaceId: string;
  /**
   * Which workflows to show, from the address. `undefined` is "all" — see
   * `workflowListSearchSchema`. Owned by the route rather than by this page
   * because the choice is shareable and survives a reload, and because
   * Overview's drafts count links straight into it.
   */
  statusFilter?: WorkflowStatusFilter;
  onStatusFilterChange: (next: WorkflowStatusFilter | undefined) => void;
}

export function WorkflowListPage({
  workspaceId,
  statusFilter,
  onStatusFilterChange,
}: WorkflowListPageProps) {
  const { workflows, isPending, loadFailed, refetch } =
    useWorkflows(workspaceId);
  // A placeholder only once the wait is worth remarking on (spec 079 FR-017).
  const showPlaceholder = usePendingDisplay(isPending);
  // Navigation animates only for readers who have not asked for less of it
  // (spec 075 FR-031). The stylesheet cannot suppress a view transition, so
  // this one is asked in JavaScript.
  const reducedMotion = useReducedMotion();
  const canWork = useCanWork();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // At most one row is being renamed, so the list owns which — the row menu
  // starts it and the name control ends it.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  // Stays component-local, unlike the status filter: nothing links to a search
  // term, and every keystroke would otherwise become a history entry.
  const [query, setQuery] = useState('');

  // All of this runs over the array the page already holds: the list is one
  // workspace's workflows, read in a single aggregate query, so narrowing it
  // is a filter and never a second request.
  const needle = query.trim().toLowerCase();
  const visible = workflows.filter(
    (workflow) =>
      (statusFilter === undefined ||
        (statusFilter === 'drafts'
          ? workflow.status === 'draft'
          : workflow.status === 'published')) &&
      (needle === '' ||
        workflow.name.toLowerCase().includes(needle) ||
        workflow.key.toLowerCase().includes(needle)),
  );
  const isFiltered = needle !== '' || statusFilter !== undefined;
  const draftCount = workflows.filter(
    (workflow) => workflow.status === 'draft',
  ).length;
  const publishedCount = workflows.length - draftCount;
  const openUpload = () => setUploadOpen(true);
  const openCreate = () => setCreateOpen(true);

  return (
    <PagePlaceholder
      eyebrow="Workspace"
      title="Workflows"
      // Not "each one owns a board": a draft has no board and cannot hold a
      // task, which is the whole reason the list marks drafts at all.
      description="The workflows installed in this workspace. A published one has a board and holds its own tasks; a draft stays in the builder until you publish it."
      icon={WorkflowIcon}
      actions={
        <AddWorkflowMenu
          workspaceId={workspaceId}
          onCreateFromScratch={openCreate}
          onUploadFromYaml={openUpload}
          // The page's primary action: filled and labelled, the same control
          // the empty state offers. It used to shrink to an unlabelled outline
          // icon as soon as one workflow existed, so the main action on the
          // page got quieter exactly as the page got busier.
          trigger={
            <Button size="sm" data-phone-row className="max-md:w-full">
              <PlusIcon aria-hidden="true" />
              Add workflow
            </Button>
          }
        />
      }
    >
      {isPending ? (
        showPlaceholder && (
          // Rows-shaped placeholders rather than a centred sentence: the arriving
          // list lands where the eye already is, instead of the whole region
          // being swapped and the page jumping under the reader.
          <PendingRegion label="Loading workflows" className="gap-2">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                data-testid="workflow-row-skeleton"
                aria-hidden="true"
                className="border-border flex items-center gap-3 rounded-lg border p-3"
              >
                <Skeleton className="size-2 shrink-0 rounded-full" />
                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3 w-40 rounded-sm" />
                  <Skeleton className="h-2.5 w-64 rounded-sm" />
                </span>
                <Skeleton className="h-7 w-28 shrink-0 rounded-md" />
              </div>
            ))}
          </PendingRegion>
        )
      ) : loadFailed ? (
        <EmptyState
          icon={WorkflowIcon}
          description="The workflows could not be loaded."
          action={<RetryButton onRetry={() => void refetch()} />}
        />
      ) : workflows.length === 0 ? (
        <EmptyState
          icon={WorkflowIcon}
          description="No workflows exist in this workspace yet. Add the first one to get a board, and a place for its tasks."
          action={
            <AddWorkflowMenu
              workspaceId={workspaceId}
              onCreateFromScratch={openCreate}
              onUploadFromYaml={openUpload}
              trigger={<Button size="sm">Add workflow</Button>}
            />
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="Filter workflows"
              placeholder="Filter by name or key"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              className="h-8 w-full max-w-xs text-sm max-md:max-w-none"
            />
            {/* Status is the axis worth a control of its own: it decides
                whether a workflow has a board at all. */}
            <div
              role="group"
              aria-label="Show"
              // On a phone the three split the line (spec 273).
              className="flex items-center gap-1 max-md:w-full max-md:[&>*]:flex-1"
            >
              {STATUS_FILTERS.map((option) => (
                <Button
                  key={option.label}
                  size="sm"
                  variant={
                    statusFilter === option.value ? 'secondary' : 'ghost'
                  }
                  aria-pressed={statusFilter === option.value}
                  onClick={() => {
                    onStatusFilterChange(option.value);
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <p className="text-muted-foreground ml-auto text-xs tabular-nums">
              {isFiltered
                ? `Showing ${visible.length} of ${workflows.length} ${workflows.length === 1 ? 'workflow' : 'workflows'}`
                : `${workflows.length} ${workflows.length === 1 ? 'workflow' : 'workflows'} · ${publishedCount} published, ${draftCount} ${draftCount === 1 ? 'draft' : 'drafts'}`}
            </p>
          </div>

          {/* A filter that matches nothing is not an empty workspace, and must
              not borrow the empty state's invitation to add a first workflow —
              there are workflows here, just not these. */}
          {visible.length === 0 ? (
            <EmptyState
              icon={WorkflowIcon}
              description="No workflows match this filter."
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setQuery('');
                    onStatusFilterChange(undefined);
                  }}
                >
                  Clear filter
                </Button>
              }
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {visible.map((workflow) => {
                const stepCount = countDeclaredSteps(workflow.phases);
                const phaseCount = workflow.phases.length;
                const isDraft = workflow.status === 'draft';
                return (
                  <li
                    key={workflow.id}
                    // On a phone, where the row's Open board button is hidden,
                    // the whole card opens the workflow and the status dot and
                    // ⋯ line up with the name (spec 273).
                    className="border-border hover:bg-accent/60 focus-within:bg-accent/60 flex items-center gap-3 rounded-lg border p-3 transition-colors max-md:relative max-md:items-start"
                  >
                    {/* Status leads, because it decides everything else about the
                    row: a draft has no board and cannot hold a task. Drafts
                    say so in words beside this marker, so colour is never the
                    only channel; published rows have no word, so their marker
                    carries the name instead. */}
                    {isDraft ? (
                      <span
                        aria-hidden="true"
                        className="bg-workflow-draft size-2 shrink-0 rounded-full max-md:mt-1.5"
                      />
                    ) : (
                      <span
                        role="img"
                        aria-label="Published"
                        className="bg-workflow-published size-2 shrink-0 rounded-full max-md:mt-1.5"
                      />
                    )}
                    <span className="flex min-w-0 flex-1 flex-col leading-tight">
                      {/* On a phone the badges drop under the name rather than
                      squeezing it to "Specificatio…" (spec 265). */}
                      <span className="flex min-w-0 items-center gap-2 max-md:flex-wrap max-md:gap-y-1">
                        {/* The name is the link to the workflow's graph. It no
                        longer stretches over the row: the row now carries an
                        explicit primary action, and an overlay would have to
                        be cut away from every control that joins it. */}
                        <WorkflowName
                          workspaceId={workspaceId}
                          workflowId={workflow.id}
                          name={workflow.name}
                          className="min-w-0 text-sm font-medium"
                          isEditing={renamingId === workflow.id}
                          onEditingChange={(editing) => {
                            setRenamingId(editing ? workflow.id : null);
                          }}
                          renderDisplay={(displayName) => (
                            <Link
                              // Where the workflow can actually be used: published
                              // to its board, draft to its builder (spec 094
                              // FR-007, FR-008). It opened the diagram before, on
                              // every row — but the reason to open a workflow is
                              // overwhelmingly to see the work moving through it,
                              // and the diagram is now beside the board rather
                              // than in front of it.
                              {...workflowDestination(workflow, workspaceId)}
                              // The name reaches a board as often as a builder,
                              // and asked for neither transition until now.
                              viewTransition={!reducedMotion}
                              // The name itself carries into the page's heading
                              // (spec 075 FR-026), keyed on the workflow's own
                              // id. On the row around it, which is a flex line
                              // filling the list, the browser was morphing a
                              // box the width of the page — mostly empty, and
                              // carrying the version badge as well — into a
                              // heading the width of a few words. That reads as
                              // the row collapsing rather than the name moving.
                              style={{
                                viewTransitionName: `workflow-${workflow.id}`,
                              }}
                              // Two workflows may share a display name (FR-030), so
                              // the key is what tells their links apart when read
                              // aloud.
                              aria-label={`Open ${displayName} (${workflow.key})`}
                              className="text-foreground focus-visible:ring-ring touch-pad truncate rounded-sm hover:underline focus-visible:ring-3 focus-visible:outline-none max-md:after:absolute max-md:after:inset-0"
                            >
                              {displayName}
                            </Link>
                          )}
                        />
                        {isDraft && (
                          // Says why this row has no board, rather than leaving its
                          // absence to be discovered (FR-003).
                          <Badge variant="outline" className="shrink-0">
                            Draft
                          </Badge>
                        )}
                        {/* A published workflow with unpublished changes
                            (spec 144 FR-010). */}
                        <PendingChangesMarker
                          workflow={workflow}
                          render={
                            <Badge variant="outline" className="shrink-0" />
                          }
                        />
                        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                          v{workflow.version}
                        </span>
                      </span>
                      {/* The description is what the author wrote about the
                      workflow, and the only line that says what it is *for*.
                      The key is the fallback rather than the default: it is
                      derivable from the name, so it earns the line only when
                      nothing better is there to occupy it. */}
                      <span className="text-muted-foreground truncate text-xs">
                        {workflow.description ? (
                          workflow.description
                        ) : (
                          <code>{workflow.key}</code>
                        )}
                      </span>
                    </span>
                    <span className="text-muted-foreground hidden shrink-0 text-right text-xs leading-tight tabular-nums sm:block">
                      {/* Phases first: they are the structure an author works in,
                      and the builder is organised by them. */}
                      <span className="text-foreground block">
                        {phaseCount} {phaseCount === 1 ? 'phase' : 'phases'}
                      </span>
                      <span className="block">
                        {stepCount} {stepCount === 1 ? 'step' : 'steps'}
                      </span>
                    </span>
                    {/* One action per row, and which one it is follows the status:
                    (On a phone the name is that action: it opens the same board
                    or builder, so the button goes and the name keeps the width,
                    spec 265.)
                    a draft's board does not exist, and its unfinished
                    definition is the thing anybody opening it wants. Someone
                    only reading the workspace cannot continue it: the same
                    route opens the builder's read-only view for them, so the
                    button says so (spec 193 FR-008). */}
                    {isDraft ? (
                      <Link
                        to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
                        params={{ workspaceId, workflowKey: workflow.key }}
                        viewTransition={!reducedMotion}
                        className={cn(
                          buttonVariants({ variant: 'outline', size: 'sm' }),
                          'shrink-0 max-md:hidden',
                        )}
                      >
                        {canWork ? 'Continue in builder' : 'View'}
                      </Link>
                    ) : (
                      <Link
                        to="/workspaces/$workspaceId/workflows/$workflowKey/board"
                        params={{ workspaceId, workflowKey: workflow.key }}
                        viewTransition={!reducedMotion}
                        className={cn(
                          buttonVariants({ variant: 'outline', size: 'sm' }),
                          'shrink-0 max-md:hidden',
                        )}
                      >
                        Open board
                      </Link>
                    )}
                    <WorkflowRowMenu
                      workflow={workflow}
                      workspaceId={workspaceId}
                      onRename={() => {
                        setRenamingId(workflow.id);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <CreateWorkflowDialog
        workspaceId={workspaceId}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
      <UploadWorkflowDialog
        workspaceId={workspaceId}
        workflows={workflows}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
    </PagePlaceholder>
  );
}
